import type { MaybeRefOrGetter } from '@vueuse/core';
import type { ShareMethod } from '@/enums/share-methods';
import type { ProfileInfoFragment } from '@/features/user-profile/graphql/fragments/ProfileInfo.fragment';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { TextRecommendationFragment } from '@/features/why-to-watch/graphql/fragments/TextRecommendation.fragment';
import type {
	LikeTextRecommendationMutation,
	LikeTextRecommendationMutationVariables,
} from '@/features/why-to-watch/graphql/mutations/LikeTextRecommendation.mutation';

import { TextRecommendationFragmentDoc } from '@/features/why-to-watch/graphql/fragments/TextRecommendation.fragment';
import { LikeTextRecommendationDocument } from '@/features/why-to-watch/graphql/mutations/LikeTextRecommendation.mutation';

import { ShareHelper, trackShareEvent } from '@/helpers/share-helper';
import { formatDateTime } from '@/helpers/date-helper';

import { useLanguageStore } from '@/helpers/composables/useStores';
import { useMutation } from '@/helpers/composables/useApollo';
import { useI18n } from '@/helpers/composables/useI18n';

import { toValue } from '@vueuse/core';
import { computed } from 'vue';

interface UseTextRecommendationOptions {
	textRecommendation: MaybeRefOrGetter<TextRecommendationFragment>;
	title?: MaybeRefOrGetter<TitleDetail>;
}

export function useTextRecommendation({ textRecommendation, title }: UseTextRecommendationOptions) {
	const { t } = useI18n();
	const languageStore = useLanguageStore();

	const likedByUser = computed(() => toValue(textRecommendation).likedByUser ?? false);
	const likedCount = computed(() => toValue(textRecommendation).likeCount ?? 0);
	const ownedByUser = computed(() => toValue(textRecommendation).ownedByUser ?? 0);

	const textRecTitle = computed(() => toValue(title) ?? toValue(textRecommendation).title);

	const profile = computed(() => toValue(textRecommendation).profile);

	const hasTags = computed(() => (toValue(textRecommendation).tags ?? []).length > 0);

	const tagsString = computed(() => {
		const { tags } = toValue(textRecommendation);
		if (tags == null || tags.length === 0) return null;

		return tags.map(tag => `#${tag.translatedName}`).join(' ');
	});

	const date = computed(() =>
		formatDateTime(
			new Date(toValue(textRecommendation).watchedAt),
			languageStore.language.value,
			languageStore.country.value,
			{ day: '2-digit', month: '2-digit', year: 'numeric' }
		)
	);

	const watchedInCinema = computed(() => toValue(textRecommendation).watchedOn?.packageId === 0);

	const watchedOnText = computed(() => {
		if (watchedInCinema.value) return t('WEBAPP_WATCHED_CINEMA', { date: date.value });

		return t('WEBAPP_WATCHED_PROVIDER', {
			provider: toValue(textRecommendation).watchedOn?.clearName,
			date: date.value,
		});
	});

	const shareId = computed(() => getTextRecAnchor(toValue(textRecommendation).profile));

	function trackShareClicked(isOpen?: boolean) {
		if (textRecTitle.value == null || isOpen !== true) return;

		trackShareEvent(textRecTitle.value, {
			action: 'wtw_click',
			property: toValue(textRecommendation).id,
		});
	}

	function shareRecommendation(method: ShareMethod) {
		if (textRecTitle.value == null) return;

		ShareHelper.share({
			title: textRecTitle.value,
			tracking: {
				action: 'wtw_share',
				label: method,
				property: toValue(textRecommendation).id,
			},
			content: {
				url: `${JW_CONFIG.DOMAIN}${textRecTitle.value.content.fullPath}#${shareId.value}`,
				text: `"${toValue(textRecommendation)?.body}" – ${toValue(textRecommendation)?.profile.displayName}`,
				method,
			},
			useNative: false,
		});
	}

	const { mutate: likeRecommendationMutation } = useMutation<
		LikeTextRecommendationMutation,
		LikeTextRecommendationMutationVariables
	>(LikeTextRecommendationDocument, () => ({
		refetchQueries: ['GetTextRecommendations'],
		update: (cache, { data }) => {
			if (data?.likeTextRecommendationV2 == null) return;

			const { textRecommendation } = data.likeTextRecommendationV2;
			cache.writeFragment({
				id: textRecommendation.id,
				data: textRecommendation,
				fragmentName: 'TextRecommendation',
				fragment: TextRecommendationFragmentDoc,
				variables: { excludeTextRecommendationTitle: false },
			});
		},
	}));

	const textRecommendationHasVideo = computed(() => !!toValue(textRecommendation).video);

	return {
		profile,

		shareId,
		textRecTitle,

		textRecommendationHasVideo,

		likedByUser,
		likedCount,
		ownedByUser,

		hasTags,
		tagsString,
		watchedOnText,

		trackShareClicked,
		shareRecommendation,

		likeRecommendationMutation,
	};
}

export function getTextRecAnchor(profile: MaybeRefOrGetter<ProfileInfoFragment>) {
	return toValue(profile)
		.displayName.toLowerCase()
		.replace(/[^a-zA-Z ]/g, '') // remove all special characters (keep spaces)
		.replaceAll(' ', '-');
}
